import React, { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ChevronLeftIcon,
  XMarkIcon,
  CheckIcon,
  ArrowRightIcon,
  ForwardIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import {
  useWebSearchResults,
  useWebSearchJobStatus,
  usePromoteWebSearchResult,
  useUpdateWebSearchResultStatus,
  WebSearchResult,
} from "../api/WebSearchAPI";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import Button, { ButtonColorTypes } from "../shared-components/Button";
import ExtensionInstallBanner from "../shared-components/ExtensionInstallBanner";
import {
  useExtensionStatus,
  useWebSearchExtensionBridge,
  ExtensionRequiredModal,
  WebSearchNewsSubmitPayload,
} from "../services/extension";
import { useReportByUUID } from "../api/ReportsApi";
import { useGetPersonInfo } from "../api/PersonApi";
import { transformPersonInfo } from "./utils/personInfoTransform";
import {
  buildWebSearchWorkbenchNavigationState,
  pickWorkbenchNavigationState,
} from "./workbenchEntrySource";
import {
  collectPiiOffsets,
  segmentsFromOffsets,
} from "./webSearchHighlightUtils";
import Reports360NotesAutosave from "./docs/Reports360NotesAutosave";
import { classNames } from "../utils";

// Toast Components
function ItemSkippedToast({
  isVisible,
  onClose,
}: {
  isVisible: boolean;
  onClose: () => void;
}) {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => {
          onClose();
        }, 300);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out ${
        isExiting
          ? "translate-y-[-100%] opacity-0"
          : "translate-y-0 opacity-100"
      }`}
    >
      <div className="bg-white flex gap-4 items-center overflow-hidden px-6 py-5 rounded-lg shadow-md w-[405px]">
        <div className="flex flex-1 gap-4 items-center">
          <div className="bg-[#E5E7EB] relative rounded-full shrink-0 size-[40px] flex items-center justify-center">
            <ArrowRightIcon className="w-6 h-6 text-[#4B5563]" />
          </div>
          <p className="flex-1 font-medium leading-[28px] text-[#30363f] text-lg">
            Item has been Skipped
          </p>
          <button
            onClick={() => {
              setIsExiting(true);
              setTimeout(() => onClose(), 300);
            }}
            className="text-[#4B5563] hover:text-gray-800 shrink-0"
            aria-label="Close"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
}

function ItemAddedToast({
  isVisible,
  onClose,
}: {
  isVisible: boolean;
  onClose: () => void;
}) {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => {
          onClose();
        }, 300);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <div
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out ${
        isExiting
          ? "translate-y-[-100%] opacity-0"
          : "translate-y-0 opacity-100"
      }`}
    >
      <div className="bg-white flex gap-4 items-center overflow-hidden px-6 py-5 rounded-lg shadow-md w-[405px]">
        <div className="flex flex-1 gap-4 items-center">
          <div className="bg-[#D1FAE5] relative rounded-full shrink-0 size-[40px] flex items-center justify-center">
            <CheckIcon className="w-6 h-6 text-[#2E764E]" />
          </div>
          <p className="flex-1 font-medium leading-[28px] text-[#30363f] text-lg">
            Item has been added
          </p>
          <button
            onClick={() => {
              setIsExiting(true);
              setTimeout(() => onClose(), 300);
            }}
            className="text-[#4B5563] hover:text-gray-800 shrink-0"
            aria-label="Close"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function WebResultsView() {
  const { id: report_uuid } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<"results" | "reviewed">("results");
  const [showSkippedToast, setShowSkippedToast] = useState(false);
  const [showAddedToast, setShowAddedToast] = useState(false);
  const [extensionModalDismissed, setExtensionModalDismissed] = useState(false);
  const [optionalUpdateModalRequested, setOptionalUpdateModalRequested] =
    useState(false);
  const [investigatorNotesStatus, setInvestigatorNotesStatus] = useState("");

  // Get runExternalId from location state when navigating from web search.
  const runExternalId =
    (location.state as { runExternalId?: string })?.runExternalId || null;

  // Get reportId from job status
  const { data: jobStatus, isLoading: isLoadingJobStatus } =
    useWebSearchJobStatus(runExternalId, !!runExternalId);
  const reportId = jobStatus?.report_id || null;

  const jobAwaitingCompletion =
    !!runExternalId &&
    !!jobStatus &&
    ["running", "partial"].includes((jobStatus.status || "").toLowerCase());

  const fetchResultsEnabled = !!reportId && !jobAwaitingCompletion;

  const {
    data: allResults,
    isLoading: isLoadingResults,
    refetch,
  } = useWebSearchResults(reportId || 0, fetchResultsEnabled);

  // Item-service enriches pii_matches / relevance_score after the web-search job is terminal.
  // One delayed refetch picks up those PATCHes without continuous polling.
  const ENRICHMENT_REFETCH_DELAY_MS = 4500;
  useEffect(() => {
    if (!fetchResultsEnabled || !runExternalId || !jobStatus) {
      return;
    }
    const statusLower = (jobStatus.status || "").toLowerCase();
    if (statusLower === "running" || statusLower === "partial") {
      return;
    }
    const timer = window.setTimeout(() => {
      void refetch();
    }, ENRICHMENT_REFETCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [fetchResultsEnabled, runExternalId, jobStatus, refetch]);

  // Loading: job status in flight, no report id yet, job still running/partial, or results query loading
  const isLoading =
    isLoadingJobStatus ||
    (!!runExternalId && !reportId) ||
    jobAwaitingCompletion ||
    (fetchResultsEnabled && isLoadingResults);

  const { mutateAsync: promoteResult } = usePromoteWebSearchResult();
  const { mutateAsync: updateStatus, isLoading: isUpdatingStatus } =
    useUpdateWebSearchResultStatus();

  const {
    status: extensionStatus,
    sendWebSearchContext,
    sendSubmitResponse,
    setOnSubmit,
    reconnect: reconnectExtension,
  } = useWebSearchExtensionBridge();

  // Get release info for download URL (defaults to prod channel when not installed)
  // Use recheckExtensionWithRetry for "Check Again" - it has better reliability after install/update
  const { updateInfo, connectionStatus, recheckExtensionWithRetry } =
    useExtensionStatus();
  const extensionDownloadUrl = updateInfo?.downloadUrl || null;

  // Fetch report data to get person_uuid
  const { data: reportData, mutate: reportDataGet } = useReportByUUID();
  const personUuid = reportData?.person_uuid;

  // Fetch report data when report_uuid is available
  // Refetch if report_uuid changes (e.g., navigating to different report)
  useEffect(() => {
    if (report_uuid && reportData?.report_uuid !== report_uuid) {
      reportDataGet(report_uuid);
    }
  }, [report_uuid, reportData, reportDataGet]);

  // Fetch person info for PII highlighting
  const {
    data: personEx,
    mutateAsync: personExGetAsync,
    reset: resetPersonEx,
  } = useGetPersonInfo();

  // Track which personUuid the current personEx belongs to
  // This prevents using stale data when navigating between reports
  const personExUuidRef = useRef<string | null>(null);
  // Track the UUID that was requested when the current in-flight request was made
  // This prevents race conditions where stale responses arrive after personUuid changes
  const requestedPersonUuidRef = useRef<string | null>(null);
  // Request counter to track the latest request and reject stale responses
  // Incremented each time we make a new request, so we can identify which response is current
  const requestCounterRef = useRef<number>(0);

  // Fetch person info when personUuid is available
  // Refetch if personUuid changes (e.g., navigating to different report)
  useEffect(() => {
    if (personUuid) {
      // If personUuid changed, clear stale data before fetching new data
      if (personExUuidRef.current !== personUuid) {
        resetPersonEx();
        personExUuidRef.current = null;
      }
      // Increment request counter to mark this as a new request
      // This ensures any stale responses from previous requests will be rejected
      requestCounterRef.current += 1;
      const currentRequestId = requestCounterRef.current;
      const requestPersonUuid = personUuid; // Capture personUuid at request time
      // Track which UUID we're requesting (captured at request time)
      requestedPersonUuidRef.current = personUuid;

      // Make the request and verify the response is still current when it arrives
      personExGetAsync(personUuid)
        .then(() => {
          // Only accept this response if:
          // 1. This is still the latest request (counter hasn't incremented)
          // 2. personUuid hasn't changed since we made the request
          // 3. The requested UUID still matches what we captured at request time
          if (
            currentRequestId === requestCounterRef.current &&
            requestedPersonUuidRef.current === requestPersonUuid &&
            requestPersonUuid !== null
          ) {
            personExUuidRef.current = requestPersonUuid;
          }
        })
        .catch(() => {
          // Ignore errors - they don't affect the race condition fix
        });
    } else {
      // Clear data and refs when personUuid is not available
      resetPersonEx();
      personExUuidRef.current = null;
      requestedPersonUuidRef.current = null;
      // Increment counter to invalidate any in-flight requests
      requestCounterRef.current += 1;
    }
  }, [personUuid, personExGetAsync, resetPersonEx]);

  // Note: personExUuidRef is now set in the promise handler of personExGetAsync
  // which ensures we only accept responses from the current request. This effect
  // is no longer needed since the promise handler provides the race condition protection.
  // We keep personEx in the dependency array of handleView to ensure it's up to date,
  // but the validation happens in the promise handler.

  const handleExtensionSubmit = useCallback(
    async (
      payload: WebSearchNewsSubmitPayload,
      meta: { sourceTabId?: number }
    ) => {
      console.info("[WebSearch] 📥 Received NEWS_SUBMIT from extension", {
        payload_report_uuid: payload.report_uuid,
        payload_result_external_id: payload.result_external_id,
        payload_title: payload.override_title?.slice(0, 50),
        sourceTabId: meta.sourceTabId,
        current_report_uuid: report_uuid,
        current_reportId: reportId,
      });

      // Guard: Report UUID must match
      if (payload.report_uuid !== report_uuid) {
        console.warn("[WebSearch] ❌ REJECTED: Report UUID mismatch", {
          expected: report_uuid,
          received: payload.report_uuid,
        });
        sendSubmitResponse({
          tabId: meta.sourceTabId,
          ok: false,
          reason: "Report mismatch",
        });
        return;
      }

      // Guard: Result must exist and belong to this report
      const result = allResults?.find(
        (item) => item.external_id === payload.result_external_id
      );
      if (!result || !reportId || result.report_id !== reportId) {
        console.warn(
          "[WebSearch] ❌ REJECTED: Result not found or wrong report",
          {
            result_external_id: payload.result_external_id,
            resultFound: !!result,
            result_report_id: result?.report_id,
            expected_reportId: reportId,
          }
        );
        sendSubmitResponse({
          tabId: meta.sourceTabId,
          ok: false,
          reason: "Result does not belong to this report",
        });
        return;
      }

      console.info("[WebSearch] ✅ Guards passed, promoting result", {
        result_external_id: payload.result_external_id,
        result_title: result.title?.slice(0, 50),
        override_title: payload.override_title?.slice(0, 50),
      });

      try {
        await promoteResult({
          result_external_id: payload.result_external_id,
          overrides: {
            override_title: payload.override_title,
            override_url: payload.override_url,
            override_author: payload.override_author || null,
            override_date: payload.override_date || null,
            override_body: payload.override_body,
          },
        });
        console.info("[WebSearch] ✅ Result promoted successfully", {
          result_external_id: payload.result_external_id,
        });
        setShowAddedToast(true);
        refetch();
        sendSubmitResponse({ tabId: meta.sourceTabId, ok: true });
      } catch (error) {
        console.error("[WebSearch] ❌ Failed to promote result", {
          result_external_id: payload.result_external_id,
          error,
        });
        sendSubmitResponse({
          tabId: meta.sourceTabId,
          ok: false,
          reason: "Failed to promote result",
        });
      }
    },
    [
      allResults,
      promoteResult,
      refetch,
      reportId,
      report_uuid,
      sendSubmitResponse,
    ]
  );

  const handleView = useCallback(
    async (result: WebSearchResult) => {
      if (!result.url) return;

      // Open tab synchronously to preserve user gesture (avoids popup blockers)
      const openedTab = window.open("about:blank", "_blank");
      if (!openedTab) return;
      try {
        openedTab.opener = null;
      } catch {
        /* noop */
      }

      // Best-effort reconnect if bridge is down (fire-and-forget)
      if (extensionStatus.status !== "connected" && report_uuid) {
        reconnectExtension();
      }

      // Best-effort: send context to extension
      if (report_uuid) {
        const validPersonEx =
          personEx && personExUuidRef.current === personUuid
            ? personEx
            : undefined;

        sendWebSearchContext({
          report_uuid,
          result_external_id: result.external_id,
          result_url: result.url,
          report_label: report_uuid.slice(-8) || report_uuid,
          prefill: {
            title: result.title,
            body: result.body,
            author: result.author,
            date: result.date,
          },
          personinfo: transformPersonInfo(validPersonEx) ?? undefined,
        });
      }

      openedTab.location.href = result.url;

      if (result.status === "unseen") {
        updateStatus({
          result_external_id: result.external_id,
          status: "viewed",
        })
          .then(() => refetch())
          .catch((err) =>
            console.error("[WebSearch] Failed to update status", err)
          );
      }
    },
    [
      extensionStatus,
      report_uuid,
      sendWebSearchContext,
      updateStatus,
      refetch,
      reconnectExtension,
      personUuid,
      personEx,
    ]
  );

  // NOTE: We intentionally do NOT clear context on unmount.
  // The context must persist so the article tab can use it.
  // Context is replaced when user clicks a new "View", or cleared
  // when the port disconnects (user closes v3-ui entirely).

  useEffect(() => {
    setOnSubmit(handleExtensionSubmit);
  }, [handleExtensionSubmit, setOnSubmit]);

  const handleSkip = useCallback(
    async (result: WebSearchResult) => {
      try {
        await updateStatus({
          result_external_id: result.external_id,
          status: "skipped",
        });
        setShowSkippedToast(true);
        refetch();
      } catch (error) {
        console.error("Failed to skip result", error);
      }
    },
    [updateStatus, refetch]
  );

  const filteredResults =
    allResults?.filter((result) => {
      if (activeTab === "results") {
        return result.status === "unseen" || result.status === "viewed";
      } else {
        return result.status === "added" || result.status === "skipped";
      }
    }) || [];

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case "added":
        return (
          <div className="bg-[rgba(70,178,118,0.2)] flex gap-1 items-center px-2 py-0.5 rounded-md">
            <CheckIcon className="w-4 h-4 text-[#2E764E]" />
            <span className="text-[#30363f] text-sm font-medium leading-5">
              Added
            </span>
          </div>
        );
      case "skipped":
        return (
          <div className="bg-[#E5E7EB] flex gap-1 items-center px-2 py-0.5 rounded-md">
            <ForwardIcon className="w-4 h-4 text-[#4B5563]" />
            <span className="text-[#30363f] text-sm font-medium leading-5">
              Skipped
            </span>
          </div>
        );
      case "viewed":
        return (
          <div className="bg-[#DBEAFE] flex gap-1 items-center px-2 py-0.5 rounded-md">
            <EyeIcon className="w-4 h-4 text-[#1E40AF]" />
            <span className="text-[#30363f] text-sm font-medium leading-5">
              Viewed
            </span>
          </div>
        );
      default:
        return null;
    }
  };

  // Determine if extension needs install or update
  const needsInstall =
    connectionStatus === "not_installed" ||
    connectionStatus === "connection_failed";
  const needsRequiredUpdate =
    connectionStatus === "connected" && updateInfo?.status === "required";
  const needsOptionalUpdate =
    connectionStatus === "connected" && updateInfo?.status === "optional";

  // Determine modal mode based on what's needed
  const extensionModalMode = needsInstall
    ? "install"
    : needsRequiredUpdate
    ? "required_update"
    : "optional_update";
  const webSearchWorkbenchState = buildWebSearchWorkbenchNavigationState(
    location.state
  );
  const {
    activeWorkbenchTab: _activeWorkbenchTab,
    ...workbenchNavigationState
  } = pickWorkbenchNavigationState(location.state);

  return (
    <div className="flex min-h-0 h-full flex-col bg-gray-50">
      {/* Extension Required Modal - shows on first load if not installed or needs required update */}
      {/* Note: Optional updates don't auto-show modal, user clicks banner to open */}
      <ExtensionRequiredModal
        isOpen={
          (needsInstall ||
            needsRequiredUpdate ||
            (needsOptionalUpdate && optionalUpdateModalRequested)) &&
          !extensionModalDismissed
        }
        onClose={() => {
          setExtensionModalDismissed(true);
          setOptionalUpdateModalRequested(false);
        }}
        downloadUrl={extensionDownloadUrl || undefined}
        mode={extensionModalMode}
        onCheckAgain={recheckExtensionWithRetry}
      />

      <div className="px-6 pt-6">
        <button
          onClick={() =>
            navigate(`/360/${report_uuid}/workbench`, {
              state: workbenchNavigationState,
            })
          }
          className="inline-flex items-center gap-1 text-gray-700 hover:text-gray-900"
        >
          <ChevronLeftIcon className="w-6 h-6" />
          <span className="text-lg font-medium">Back to Workbench</span>
        </button>
      </div>

      <div className="px-6 pt-6 pb-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-[36px] font-bold text-gray-900 leading-none">
            Review Web Results
          </h1>
          <Button
            text="Complete Review"
            onClick={() =>
              navigate(`/360/${report_uuid}/workbench`, {
                state: webSearchWorkbenchState,
              })
            }
            colorType={ButtonColorTypes.primary}
            className="text-base font-semibold px-3 py-2"
          />
        </div>
        <div className="bg-red-600 text-white py-2 px-4 rounded-lg mb-4 text-center">
          REMINDER: Please make sure you start the timer before doing
          investigating work!
        </div>

        {needsInstall && (
          <ExtensionInstallBanner
            title="Install Salvage Yard to add articles"
            description="Install the Salvage Yard extension to edit and add articles to this report."
            actionLabel="Get Extension"
            onClick={() => setExtensionModalDismissed(false)}
          />
        )}
        {needsRequiredUpdate && (
          <ExtensionInstallBanner
            title="Update Salvage Yard to continue"
            description="A newer version is required to add articles to this report."
            actionLabel="Update Extension"
            onClick={() => setExtensionModalDismissed(false)}
          />
        )}
        {needsOptionalUpdate && (
          <ExtensionInstallBanner
            title="Update available for Salvage Yard"
            description={`Version ${updateInfo?.latestVersion} is available. We recommend staying up to date.`}
            actionLabel="Get Update"
            onClick={() => {
              setExtensionModalDismissed(false);
              setOptionalUpdateModalRequested(true);
            }}
          />
        )}
      </div>

      <div className="flex flex-1 min-h-0 gap-6 px-6">
        <div className="flex min-h-0 w-[38%] max-w-[440px] shrink-0 flex-col overflow-hidden">
          <div className="mb-3 flex shrink-0 items-center justify-between gap-2 pb-2 z-[1]relative">
            <h2 className={classNames("text-lg font-semibold text-green-600")}>
              Investigator&apos;s Notes
            </h2>
            {investigatorNotesStatus ? (
              <span className="text-right text-sm text-gray-500">
                {investigatorNotesStatus}
              </span>
            ) : null}
            <div className="h-0.5 bg-green-600 w-48 bottom-0 left-0 z-10 absolute" />
            <div className="h-px bg-gray-200 w-full absolute bottom-0 left-0 z-[1]" />
          </div>
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-white">
            {report_uuid ? (
              <Reports360NotesAutosave
                reportId={report_uuid}
                onStatusChange={setInvestigatorNotesStatus}
                showStatusInline={false}
              />
            ) : (
              <p className="text-sm text-gray-500">Loading report…</p>
            )}
          </div>
        </div>

        <section className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="flex flex-col gap-2 relative mb-4">
            <div className="flex gap-7 items-center">
              <button
                onClick={() => setActiveTab("results")}
                className="flex flex-col gap-2 relative pb-2"
              >
                <span
                  className={`text-lg font-semibold ${
                    activeTab === "results" ? "text-green-600" : "text-gray-400"
                  }`}
                >
                  Results
                </span>
                {activeTab === "results" && (
                  <div className="h-0.5 bg-green-600 w-full absolute bottom-0 left-0 z-10" />
                )}
              </button>
              <button
                onClick={() => setActiveTab("reviewed")}
                className="flex flex-col gap-2 relative pb-2"
              >
                <span
                  className={`text-lg font-medium ${
                    activeTab === "reviewed"
                      ? "text-green-600"
                      : "text-gray-400"
                  }`}
                >
                  Reviewed
                </span>
                {activeTab === "reviewed" && (
                  <div className="h-0.5 bg-green-600 w-full absolute bottom-0 left-0 z-10" />
                )}
              </button>
            </div>
            <div className="h-px bg-gray-200 w-full absolute bottom-0 left-0 z-[1]" />
          </div>
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : filteredResults.length === 0 ? (
            <div
              className={classNames(
                `w-full rounded-md p-8 shadow-md flex-1  overflow-y-auto`,
                activeTab === "reviewed"
                  ? "bg-[#E5E7EB]"
                  : "border border-gray-200 bg-white"
              )}
            >
              <div className="flex w-full flex-col items-start gap-0">
                <p
                  className={`text-left text-base font-normal ${
                    activeTab === "reviewed"
                      ? "text-[#111827]"
                      : "text-[#6B7280]"
                  }`}
                >
                  {activeTab === "reviewed"
                    ? "No reviewed items yet"
                    : "No results to display"}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex w-full flex-col justify-start gap-7 flex-1 overflow-y-auto bg-white p-4 rounded-lg shadow-md">
              {filteredResults.map((result) => {
                const displayBody = result.body || result.description || "";
                const piiRanges = collectPiiOffsets(result.pii_matches);
                const bodySegments = segmentsFromOffsets(
                  displayBody,
                  piiRanges
                );
                const piiTiles = [
                  ...(result.pii_matches?.names ?? []),
                  ...(result.pii_matches?.institutions ?? []),
                ].filter((e) => e.text?.trim());
                const scoreNum =
                  result.relevance_score != null &&
                  !Number.isNaN(Number(result.relevance_score))
                    ? Number(result.relevance_score)
                    : null;

                return (
                  <div
                    key={result.id}
                    className="flex w-full max-w-[800px] flex-col gap-7 rounded-lg bg-white p-6 outline outline-1 outline-offset-[-1px] outline-gray-300"
                  >
                    <div className="flex flex-col items-start gap-4">
                      {/* Card Header: Title and Action Buttons Row */}
                      <div className="flex flex-col items-start gap-2 w-full">
                        <div className="flex items-center gap-2 w-full flex-wrap">
                          <h3 className="flex-1 text-gray-700 text-lg font-bold min-w-0">
                            {result.title}
                          </h3>
                          {result.date ? (
                            <span className="text-sm text-gray-500 shrink-0">
                              {result.date}
                            </span>
                          ) : null}
                          {activeTab === "reviewed" &&
                            renderStatusBadge(result.status)}
                          {activeTab === "results" &&
                            result.status !== "added" &&
                            result.status !== "skipped" && (
                              <div className="flex shrink-0 items-center gap-2">
                                <button
                                  onClick={() => handleSkip(result)}
                                  className="text-gray-500 hover:text-gray-700"
                                  aria-label="Skip"
                                  disabled={isUpdatingStatus}
                                >
                                  <ForwardIcon className="h-6 w-6" />
                                </button>
                              </div>
                            )}
                        </div>
                        {/* URL */}
                        {result.url && (
                          <a
                            href={result.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="break-all text-base font-medium text-sky-500"
                            onClick={(e) => {
                              e.preventDefault(); // Prevent double navigation
                              handleView(result);
                            }}
                          >
                            {result.url.length > 60
                              ? `${result.url.substring(0, 60)}...`
                              : result.url}
                          </a>
                        )}
                      </div>

                      {/* Body: SERP snippet or enriched text after job completes */}
                      {displayBody ? (
                        <p className="text-black text-base font-normal whitespace-pre-wrap break-words w-full">
                          {bodySegments.map((part, i) => (
                            <span
                              key={i}
                              className={
                                part.highlight
                                  ? "bg-yellow-200 underline font-bold"
                                  : ""
                              }
                            >
                              {part.text}
                            </span>
                          ))}
                        </p>
                      ) : null}

                      {/* Relevance score (misconduct-style label + green value) */}
                      <div className="flex justify-between items-center mt-1 w-full">
                        <div className="font-bold text-black text-base">
                          Relevance Score
                          {scoreNum != null ? (
                            <span className="text-green-500 ml-2">
                              {scoreNum.toFixed(2)}
                            </span>
                          ) : (
                            <span className="text-gray-500 font-normal ml-2">
                              —
                            </span>
                          )}
                        </div>
                      </div>

                      {/* PII tiles */}
                      {piiTiles.length > 0 ? (
                        <div className="flex flex-col gap-2 w-full pt-1">
                          <span className="text-sm font-semibold text-gray-800">
                            Matched PII
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {piiTiles.map((ent, idx) => (
                              <div
                                key={`${ent.text}-${idx}`}
                                className="rounded-md bg-gray-100 border border-gray-200 px-3 py-2 text-sm text-gray-800"
                              >
                                {ent.text}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <ItemSkippedToast
        isVisible={showSkippedToast}
        onClose={() => setShowSkippedToast(false)}
      />
      <ItemAddedToast
        isVisible={showAddedToast}
        onClose={() => setShowAddedToast(false)}
      />
    </div>
  );
}
